import { DARK_COLOR_HEX_CODES, LIGHT_COLOR_HEX_CODES } from '@/constants/color'

import type { Color } from '@/types/Color'

/**
 * Converts a color string to RGBA format with the specified alpha value.
 *
 * @param color - The color string to convert. Supports hex (#fff, #ffffff), rgb(), and rgba() formats.
 * @param alpha - The alpha transparency value (0-1, will be clamped to this range).
 * @returns The color as an RGBA string, or the original color if format is unsupported.
 */
export function toRgba(color: string, alpha: number): string {
  const clamp = (n: number, min = 0, max = 1) => Math.max(min, Math.min(max, n))
  const a = clamp(alpha)
  if (/^#([0-9a-f]{3}){1,2}$/i.test(color)) {
    let r = 0,
      g = 0,
      b = 0
    if (color.length === 4) {
      r = parseInt(color[1] + color[1], 16)
      g = parseInt(color[2] + color[2], 16)
      b = parseInt(color[3] + color[3], 16)
    } else if (color.length === 7) {
      r = parseInt(color.slice(1, 3), 16)
      g = parseInt(color.slice(3, 5), 16)
      b = parseInt(color.slice(5, 7), 16)
    }
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
  if (color.startsWith('rgb(') || color.startsWith('rgba(')) {
    return color.replace(/rgba?\(([^)]+)\)/, (_m, inner) => {
      const parts = inner.split(',').map((p: string) => p.trim())
      const [r, g, b] = parts
      return `rgba(${r}, ${g}, ${b}, ${a})`
    })
  }
  return color
}

export const getRgbFromHex = (hex: string) => {
  const clean = hex.replace('#', '')
  const bigint = parseInt(clean, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return { r: Number(r), g: Number(g), b: Number(b) }
}

export const getTheme = () => {
  try {
    const classList = document.documentElement.getAttribute('class')

    if (!classList) {
      return 'light'
    }
    if (classList.includes('dark')) {
      return 'dark'
    }
    if (classList.includes('america')) {
      return 'america'
    }
    if (classList.includes('professional')) {
      return 'professional'
    }
    return 'light'
  } catch (error) {
    return 'light'
  }
}

export const getThemeAdjustedHexFromColor = (color: Color): string => {
  const theme = getTheme()
  if (theme === 'dark') return DARK_COLOR_HEX_CODES[color]
  return LIGHT_COLOR_HEX_CODES[color]
}

const getRgbValueAnalog = (value: number) => {
  return Math.max(0, 255 - value)
}

const rgbValueToHex = (value: number) => {
  return value.toString(16).padStart(2, '0')
}

export const getDarkThemeHexForLightHex = (hex: string) => {
  // if the hex is a 3-digit hex, convert it to a 6-digit hex
  if (hex.length === 3) {
    hex = hex.replace(/(\w)/g, '$1$1')
  }

  // if the hex is not a hex, return it
  if (!/^#([0-9a-f]{3}){1,2}$/i.test(hex)) {
    return hex
  }

  // if the hex starts with a #, remove it
  if (hex.startsWith('#')) {
    hex = hex.slice(1)
  }

  // convert the hex to lowercase
  hex = hex.toLowerCase()

  // get the rgb values
  const [r, g, b] = hex.match(/\w\w/g)?.map((c) => parseInt(c, 16)) || [0, 0, 0]
  const newR = getRgbValueAnalog(b)
  const newG = getRgbValueAnalog(g)
  const newB = getRgbValueAnalog(r)

  // return the new hex
  return `#${rgbValueToHex(newR)}${rgbValueToHex(newG)}${rgbValueToHex(newB)}`
}

/**
 * Brighten a hex color by a given factor (0 = no change, 1 = white).
 * factor should be between 0 and 1.
 */
export const brightenHex = (hex: string, factor: number): string => {
  const { r, g, b } = getRgbFromHex(hex)
  const brighten = (c: number) => Math.round(c + (255 - c) * factor)
  const dr = brighten(r)
  const dg = brighten(g)
  const db = brighten(b)
  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`
}

/**
 * Darken a hex color by a given factor (0 = no change, 1 = black).
 * factor should be between 0 and 1.
 */
export const darkenHex = (hex: string, factor: number): string => {
  const { r, g, b } = getRgbFromHex(hex)
  const darken = (c: number) => Math.round(c * (1 - factor))
  const dr = darken(r)
  const dg = darken(g)
  const db = darken(b)
  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`
}
